import React, { useCallback, useEffect, useState } from "react";
import { ClipboardList, ChevronDown, ChevronRight } from "lucide-react";
import { getChosenSkuBoq } from "../../services/interiorExecution";

/**
 * Read-only "Chosen SKUs" BOQ (v3_54) — the awarded items' concrete SKU + brand, prices stripped.
 * This is exactly what the buyer sees; shown here so the BV associate can confirm before it's shared.
 */
const ChosenSkuBoqCard = ({ leadId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getChosenSkuBoq(leadId));
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const total = data?.totalLines ?? 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white mb-4 overflow-hidden">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-5 py-4 text-left">
        <div className="flex items-center gap-2">
          <ClipboardList size={18} className="text-flamingo-600" />
          <span className="font-semibold text-slate-900">Chosen SKUs (buyer view)</span>
          <span className="text-xs text-slate-500">awarded items · no pricing · shared with the buyer</span>
        </div>
        <div className="flex items-center gap-3">
          {total > 0 && <span className="text-xs text-slate-500">{total} item(s)</span>}
          {open ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-400" />}
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5">
          {loading ? (
            <p className="text-sm text-slate-500 py-3">Loading…</p>
          ) : total === 0 ? (
            <p className="text-sm text-slate-500 py-3">
              No awarded items yet. This fills in as tenders are awarded to vendors.
            </p>
          ) : (
            (data.groups || []).map((g) => (
              <div key={g.packageKey} className="mb-3">
                <p className="text-sm font-semibold text-slate-800 mb-1">{g.label || g.packageKey}</p>
                <div className="border border-slate-200 rounded-xl divide-y divide-slate-100">
                  {(g.lines || []).map((l, i) => (
                    <div key={i} className="px-4 py-2 flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {l.sku || l.material}
                          {l.brand ? <span className="text-slate-500 font-normal"> · {l.brand}</span> : null}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {[l.room, l.workItem, l.material, l.materialSubcategory].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      {l.quantity != null && <span className="text-xs text-slate-500">Qty {l.quantity}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ChosenSkuBoqCard;
